/* eslint-disable @typescript-eslint/no-empty-object-type */
'use client';
import type React from 'react';
import RRProductionReportListHeader from './header';
import RRProductionReportListTable from './table';
import { useEffect, useMemo, useState } from 'react';
import { CalculationSelectionDrawer } from './calculation-selection.drawer';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  selectMaterialDetailData,
  selectMaterialDetailError,
  selectMaterialDetailLoading,
} from '@/features/match-maker/store/material-detail.selectors';
import { getMaterialDetail } from '@/features/match-maker/store/material-detail.thunk';
import {
  selectFacilityByIdData,
  selectFacilityByIdError,
  selectFacilityByIdLoading,
} from '../../store/facility.selector';
import { getFacilityById } from '../../store/facility.thunk';
import {
  ProductionLineStatusEnum,
  type ChangeCalculationParams,
  type MaterialUnitQuantities,
  type ProductionReportMeasurementInput,
} from '../../utils/types';
import { CalculationProvider } from '../../context/calculation-context';
import {
  getProductionReportById,
  postProductionReport,
} from '../../store/production-report.thunk';
import {
  selectProductionReportData,
  selectProductionReportError,
  selectProductionReportLoading,
  selectProductionReportReporting,
  selectProductionReportReportingError,
  selectProductionReportResponse,
} from '../../store/production-report.selectors';
import { resetReportState } from '../../store/production-report.slice';
import { useNotification } from '@/components/Notification/useNotification';
import { replacePlaceholders } from '@/utils/helpers';
import routes from '@/app/routes';
import {
  selectProductionPeriodDetailData,
  selectProductionPeriodDetailError,
  selectProductionPeriodDetailLoading,
} from '../../store/production-period.selectors';
import { getProductionPeriodById } from '../../store/production-period.thunk';
import {
  convertFromMeasurementToInput,
  fetchInitialMeasurements,
  fetchMaterialUnitsFromProductionOrder,
  setInitialMaterialUnits,
} from '../../utils/helpers';
import { useTagValues } from '../../api/useTagValues.hook';
import {
  selectProductionOrderByIdData,
  selectProductionOrderByIdError,
  selectProductionOrderByIdLoading,
} from '../../store/production-order.selector';
import { getProductionOrderById } from '../../store/production-order.thunk';
import { ReportConfirmationDialog } from './report-confirmation.dialog';

type ReviewReportProductionReportingWrapperProps = {};

const ReviewReportProductionReportingWrapper: React.FC<
  ReviewReportProductionReportingWrapperProps
> = () => {
  const [calculation, setCalculation] =
    useState<ChangeCalculationParams | null>(null);
  const { lineId, pid, materialId } = useParams<{
    lineId: string;
    pid: string;
    materialId: string;
  }>();
  const dispatch = useAppDispatch();
  const [measurements, setMeasurements] = useState<
    Record<string, ProductionReportMeasurementInput>
  >({});
  const [materialUnitQuantities, setMaterialUnitQuantities] =
    useState<MaterialUnitQuantities>({});
  const [isReportConfirmationDialogOpen, setIsReportConfirmationDialogOpen] =
    useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedDate = searchParams.get('date') ?? '';
  const showNotification = useNotification();
  const facilityDetail = useAppSelector(selectFacilityByIdData);
  const facilityDetailLoading = useAppSelector(selectFacilityByIdLoading);
  const facilityError = useAppSelector(selectFacilityByIdError);

  const productionPeriod = useAppSelector(selectProductionPeriodDetailData);
  const productionPeriodLoading = useAppSelector(
    selectProductionPeriodDetailLoading
  );
  const productionPeriodError = useAppSelector(
    selectProductionPeriodDetailError
  );

  const reportDetail = useAppSelector(selectProductionReportData);
  const reportLoading = useAppSelector(selectProductionReportLoading);
  const reportError = useAppSelector(selectProductionReportError);

  const materialDetail = useAppSelector(selectMaterialDetailData);
  const materialLoading = useAppSelector(selectMaterialDetailLoading);
  const materialError = useAppSelector(selectMaterialDetailError);

  const productionOrderDetail = useAppSelector(selectProductionOrderByIdData);
  const productionOrderLoading = useAppSelector(
    selectProductionOrderByIdLoading
  );
  const productionOrderError = useAppSelector(selectProductionOrderByIdError);

  const isReporting = useAppSelector(selectProductionReportReporting);
  const reportResponse = useAppSelector(selectProductionReportResponse);
  const reportReportingError = useAppSelector(
    selectProductionReportReportingError
  );

  const materialUnits = useMemo(() => {
    if (productionOrderDetail) {
      return fetchMaterialUnitsFromProductionOrder(productionOrderDetail);
    }
    return {};
  }, [productionOrderDetail]);

  const { tagValues, isTagValuesLoading } = useTagValues({
    material:
      reportDetail?.status === ProductionLineStatusEnum.REPORT_PENDING
        ? materialDetail || null
        : null,
    productionPeriod: productionPeriod || null,
    onlyPrimaryTags: true,
    materialUnits,
  });

  const isLoading = useMemo(() => {
    return (
      facilityDetailLoading ||
      productionPeriodLoading ||
      reportLoading ||
      materialLoading ||
      isTagValuesLoading ||
      productionOrderLoading
    );
  }, [
    facilityDetailLoading,
    productionPeriodLoading,
    reportLoading,
    materialLoading,
    isTagValuesLoading,
    productionOrderLoading,
  ]);

  useEffect(() => {
    if (pid) {
      dispatch(
        getProductionReportById({
          reportId: pid,
        })
      );
    }
  }, [dispatch, pid]);

  useEffect(() => {
    if (materialId) {
      dispatch(getMaterialDetail({ id: materialId }));
    }
  }, [dispatch, materialId]);

  useEffect(() => {
    if (lineId) {
      dispatch(getFacilityById({ facilityId: lineId }));
    }
  }, [dispatch, lineId]);

  useEffect(() => {
    if (pid) {
      dispatch(getProductionPeriodById(pid));
    }
  }, [dispatch, pid]);

  const validateMeasurements = () => {
    if (!pid) {
      showNotification('Missing production period id', 'destructive');
      return false;
    }
    const measurementsToSubmit = Object.values(measurements);
    if (measurementsToSubmit.length === 0) {
      showNotification('No measurements to submit', 'destructive');
      return false;
    }
    return true;
  };

  const handleSubmitReport = () => {
    if (!validateMeasurements()) {
      return;
    }
    setIsReportConfirmationDialogOpen(true);
  };

  const handleConfirmSubmitReport = () => {
    if (!pid) {
      showNotification('Missing production period id', 'destructive');
      return;
    }
    const measurementsToSubmit = Object.values(measurements);
    if (measurementsToSubmit.length === 0) {
      showNotification('No measurements to submit', 'destructive');
      return;
    }
    dispatch(
      postProductionReport({
        production_period_id: pid,
        measurements: measurementsToSubmit,
      })
    );
  };

  const measurementsCountForMaterial = useMemo(() => {
    if (!materialDetail) {
      return 0;
    }
    const components = materialDetail.components || [];
    const routings = materialDetail.routings || [];
    return components.length + routings.length + 1;
  }, [materialDetail]);

  useEffect(() => {
    if (!materialDetail) {
      return;
    }
    if (!reportDetail) {
      return;
    }
    if (reportDetail.status === ProductionLineStatusEnum.REPORT_PENDING) {
      setMaterialUnitQuantities(setInitialMaterialUnits({ materialDetail }));
      setMeasurements(
        fetchInitialMeasurements({ materialDetail, tagValues, materialUnits })
      );
    }
  }, [materialDetail, reportDetail, tagValues, materialUnits]);

  useEffect(() => {
    if (reportResponse && reportResponse.production_period_id) {
      setIsReportConfirmationDialogOpen(false);
      showNotification('Report submitted successfully', 'success');
      dispatch(resetReportState());
      navigate(
        replacePlaceholders(
          `${routes.PRODUCTION_REPORTING_ADD_PERIODS}?${searchParams.toString()}`,
          {
            lineId: lineId || '',
          }
        )
      );
    }
    if (reportReportingError) {
      showNotification('Failed to submit report', 'destructive');
    }
  }, [
    dispatch,
    lineId,
    navigate,
    reportResponse,
    searchParams,
    showNotification,
    reportReportingError,
  ]);

  useEffect(() => {
    if (reportDetail && reportDetail.measurements) {
      const measurementsMap = reportDetail.measurements.reduce(
        (acc, cur) => {
          const materialKey =
            cur.routing_id || cur.component_id || cur.material_id;
          if (!materialKey) {
            return acc;
          }
          acc[materialKey] = convertFromMeasurementToInput(cur);
          return acc;
        },
        {} as Record<string, ProductionReportMeasurementInput>
      );
      if (
        Object.keys(measurementsMap).length > 0 ||
        reportDetail.status !== ProductionLineStatusEnum.REPORT_PENDING
      ) {
        setMeasurements(measurementsMap);
      }
    }
  }, [reportDetail]);

  useEffect(() => {
    if (productionPeriod) {
      dispatch(getProductionOrderById(productionPeriod.production_order));
    }
  }, [dispatch, productionPeriod]);

  const fetchErrorStatement = useMemo(
    () =>
      [
        facilityError,
        productionPeriodError,
        materialError,
        reportError,
        productionOrderError,
      ].find(Boolean) ?? null,
    [
      facilityError,
      productionPeriodError,
      materialError,
      reportError,
      productionOrderError,
    ]
  );

  return (
    <>
      <div className="flex flex-col min-h-[calc(100vh-150px)]">
        <div className="px-6 py-6 md:px-14 flex flex-col flex-1 gap-6">
          <RRProductionReportListHeader
            selectedDate={selectedDate}
            facility={facilityDetail}
            material={materialDetail || null}
            productionPeriod={productionPeriod}
            isLoading={isLoading}
            onReportClick={handleSubmitReport}
            isReporting={isReporting}
            disableReporting={
              !materialDetail ||
              measurementsCountForMaterial !==
                Object.keys(measurements).length ||
              Object.values(measurements).some(m => m.quantity === null)
            }
          />
          <RRProductionReportListTable
            material={materialDetail || null}
            isLoading={isLoading}
            onChangeCalculation={item => {
              setCalculation(item);
            }}
            measurements={measurements}
            error={fetchErrorStatement}
            productionPeriod={productionPeriod}
          />
          <CalculationProvider
            material={calculation?.material || null}
            preMeasurement={
              measurements[calculation?.material?.id || ''] || undefined
            }
            materialUnits={materialUnits}
            materialUnitQuantities={materialUnitQuantities}
          >
            <CalculationSelectionDrawer
              isOpen={!!calculation}
              onClose={() => setCalculation(null)}
              onConfirm={measurement => {
                setMeasurements(prev => ({
                  ...prev,
                  [calculation?.material?.id || '']: {
                    ...measurement,
                    material_id: materialId || null,
                  },
                }));
                setCalculation(null);
              }}
              calculation={calculation}
              productionPeriod={productionPeriod}
              setCalculation={setCalculation}
            />
          </CalculationProvider>
        </div>
      </div>
      <ReportConfirmationDialog
        open={isReportConfirmationDialogOpen}
        isLoading={isReporting}
        onConfirm={handleConfirmSubmitReport}
        onCancel={() => {
          if (!isReporting) {
            setIsReportConfirmationDialogOpen(false);
          }
        }}
      />
    </>
  );
};

export default ReviewReportProductionReportingWrapper;
